import React, { useEffect } from "react";
import AutoAwesomeIcon from "@mui/icons-material/AutoAwesome";
import Box from "@mui/material/Box";
import Button from "@mui/material/Button";
import Divider from "@mui/material/Divider";
import Grid from "@mui/material/Grid";
import IconButton from "@mui/material/IconButton";
import TextField from "@mui/material/TextField";
import Tooltip from "@mui/material/Tooltip";
import Typography from "@mui/material/Typography";
import { SITE_BG, SURFACE } from "../../dark-theme/palette";
import { trackEvent } from "../../services/GoogleAnalytics";
import Item from "../components/Item";
import { usePagesTranslation } from "../locales";

function IdeaBlock({ title, body }) {
    return (
        <Box sx={{ mb: 3 }}>
            <Typography variant="subtitle1" sx={{ fontWeight: 600, mb: 1 }}>
                {title}
            </Typography>
            <Typography variant="body1" paragraph sx={{ fontSize: "1.1rem", lineHeight: 1.8, mb: 0 }}>
                {body}
            </Typography>
        </Box>
    );
}

function NoteRow({ title, body }) {
    return (
        <Box sx={{ mb: 2.5 }}>
            <Typography variant="subtitle1" sx={{ fontWeight: 600, mb: 1 }}>
                {title}
            </Typography>
            <Typography variant="body1" paragraph sx={{ fontSize: "1.1rem", lineHeight: 1.8, mb: 0 }}>
                {body}
            </Typography>
        </Box>
    );
}

const demoCardSx = {
    width: "100%",
    maxWidth: "560px",
    border: `2px solid ${SURFACE.border}`,
    borderRadius: "8px",
    overflow: "hidden",
    boxShadow: "0 4px 12px rgba(0,0,0,0.15)",
    backgroundColor: SURFACE.sunken,
    boxSizing: "border-box",
};

function AssistantDemoHeaderRow({ title }) {
    const { t } = usePagesTranslation();
    return (
        <Box
            sx={{
                display: "flex",
                flexDirection: "row",
                alignItems: "center",
                justifyContent: "space-between",
                gap: 1,
                width: "100%",
                boxSizing: "border-box",
                px: 2,
                py: 1.25,
                bgcolor: SURFACE.chrome,
                borderBottom: `1px solid ${SURFACE.border}`,
            }}
        >
            <Typography
                variant="subtitle1"
                component="span"
                sx={{ fontWeight: 700, color: "#3BA666", fontSize: "0.95rem", minWidth: 0, flex: "1 1 auto" }}
            >
                {title}
            </Typography>
            <Tooltip
                title={t("howto.assistant.demo-tooltip-hint")}
                arrow
                placement="top"
                componentsProps={{
                    tooltip: {
                        sx: {
                            fontSize: "0.9rem",
                            padding: "8px 12px",
                            backgroundColor: "secondary.main",
                            fontWeight: 500,
                            boxShadow: "0 4px 12px rgba(0,0,0,0.15)",
                        },
                    },
                    arrow: { sx: { color: "secondary.main" } },
                }}
            >
                <Box component="span" sx={{ display: "inline-flex", flexShrink: 0 }}>
                    <IconButton
                        size="small"
                        disabled
                        aria-label={t("puzzle.playCross.chat-with-ai", "Chat with AI")}
                        sx={{
                            backgroundColor: "secondary.main",
                            color: "#fff",
                            width: 28,
                            height: 28,
                            minWidth: 28,
                            minHeight: 28,
                            borderRadius: "50%",
                            flexShrink: 0,
                            "&.Mui-disabled": {
                                backgroundColor: "secondary.main",
                                color: "#fff",
                                opacity: 1,
                            },
                        }}
                    >
                        <AutoAwesomeIcon sx={{ fontSize: 16, color: "#fff" }} />
                    </IconButton>
                </Box>
            </Tooltip>
        </Box>
    );
}

function ClueListPlaceholder() {
    return (
        <Box sx={{ px: 2, py: 1.5, bgcolor: SURFACE.card, borderTop: `1px solid ${SURFACE.border}` }}>
            <Box sx={{ display: "flex", flexDirection: "column", gap: 1 }}>
                {[0.92, 0.78, 0.65].map((w, i) => (
                    <Box
                        key={i}
                        sx={{ height: 10, borderRadius: 1, bgcolor: SURFACE.borderStrong, width: `${Math.round(w * 100)}%`, maxWidth: "100%" }}
                    />
                ))}
            </Box>
        </Box>
    );
}

function WordsToFindListPlaceholder() {
    return (
        <Box sx={{ px: 2, py: 1.5, bgcolor: SURFACE.card, borderTop: `1px solid ${SURFACE.border}` }}>
            <Box sx={{ display: "flex", flexWrap: "wrap", gap: 0.75 }}>
                {[40, 52, 36].map((px, i) => (
                    <Box
                        key={i}
                        sx={{
                            height: 26,
                            borderRadius: "4px",
                            bgcolor: SURFACE.borderStrong,
                            width: px,
                            minWidth: 32,
                        }}
                    />
                ))}
            </Box>
        </Box>
    );
}

export default function HowToAssistant() {
    const { t } = usePagesTranslation();

    useEffect(() => {
        trackEvent("entered", { name: "HowToAssistant" });
    }, []);

    return (
        <Box sx={{ backgroundColor: SITE_BG, minHeight: "100vh", width: "100%", overflowX: "hidden" }}>
            <Grid container spacing={0} sx={{ width: "100%", maxWidth: "100%", margin: 0 }}>
                <Grid size={{ xs: 12 }} sx={{ width: "100%", maxWidth: "100%", padding: 0 }}>
                    <Item sx={{ p: { xs: 2, md: 4 }, textAlign: "left", width: "100%", maxWidth: "100%", boxSizing: "border-box" }}>
                        <Typography variant="h3" component="h1" gutterBottom sx={{ mb: 2, fontWeight: 600, color: "#1976d2" }}>
                            {t("howto.assistant.title")}
                        </Typography>

                        <Box sx={{ mb: 5 }}>
                            <Typography variant="body1" paragraph sx={{ fontSize: "1.1rem", lineHeight: 1.8 }}>
                                {t("howto.assistant.subtitle")}
                            </Typography>
                        </Box>

                        <Divider sx={{ my: 4 }} />

                        <Box sx={{ mb: 5 }}>
                            <Typography variant="h5" component="h2" gutterBottom sx={{ mb: 2, fontWeight: 600 }}>
                                {t("howto.assistant.demo-heading")}
                            </Typography>
                            {/* Crossword: Word Clues header + list area; grid puzzles: Words to Find header + word chips (same AI control). */}
                            <Box
                                sx={{
                                    display: "flex",
                                    flexDirection: "column",
                                    alignItems: "center",
                                    gap: 3,
                                    width: "100%",
                                    maxWidth: "100%",
                                    overflowX: "hidden",
                                }}
                            >
                                <Box sx={{ width: "100%", maxWidth: "560px" }}>
                                    <Typography
                                        variant="subtitle1"
                                        component="h3"
                                        sx={{ fontWeight: 600, color: "text.primary", mb: 1.5, fontSize: "1.05rem" }}
                                    >
                                        {t("howto.assistant.demo-crossword-subtitle")}
                                    </Typography>
                                    <Box sx={demoCardSx}>
                                        <AssistantDemoHeaderRow title={t("puzzle.playCross.word-clues", "Word Clues")} />
                                        <ClueListPlaceholder />
                                        <Box sx={{ px: 2, py: 1.5, bgcolor: SURFACE.card }}>
                                            <Typography
                                                variant="body2"
                                                color="text.secondary"
                                                sx={{ fontSize: "0.9rem", lineHeight: 1.6, mb: 0 }}
                                            >
                                                {t("howto.assistant.demo-crossword-mock-hint")}
                                            </Typography>
                                        </Box>
                                    </Box>
                                </Box>

                                <Box sx={{ width: "100%", maxWidth: "560px" }}>
                                    <Typography
                                        variant="subtitle1"
                                        component="h3"
                                        sx={{ fontWeight: 600, color: "text.primary", mb: 1.5, fontSize: "1.05rem" }}
                                    >
                                        {t("howto.assistant.demo-grid-subtitle")}
                                    </Typography>
                                    <Box sx={demoCardSx}>
                                        <AssistantDemoHeaderRow title={t("puzzle.playGrid.words-to-find", "Words to Find")} />
                                        <WordsToFindListPlaceholder />
                                        <Box sx={{ px: 2, py: 1.5, bgcolor: SURFACE.card }}>
                                            <Typography
                                                variant="body2"
                                                color="text.secondary"
                                                sx={{ fontSize: "0.9rem", lineHeight: 1.6, mb: 0 }}
                                            >
                                                {t("howto.assistant.demo-grid-mock-hint")}
                                            </Typography>
                                        </Box>
                                    </Box>
                                </Box>

                                <Box sx={{ width: "100%", maxWidth: "560px" }}>
                                    <Typography variant="body1" sx={{ fontSize: "1.1rem", lineHeight: 1.8, whiteSpace: "pre-line", mb: 2 }}>
                                        {t("howto.assistant.demo-caption")}
                                    </Typography>
                                    {/* Message composer: mirrors the live panel footer. Disabled here—open a puzzle to use it. */}
                                    <Box
                                        sx={{
                                            flexShrink: 0,
                                            p: 1.25,
                                            border: `2px solid ${SURFACE.border}`,
                                            borderRadius: "8px",
                                            backgroundColor: SURFACE.sunken,
                                            boxShadow: "0 4px 12px rgba(0,0,0,0.15)",
                                        }}
                                    >
                                        <Box sx={{ display: "flex", alignItems: "flex-end", gap: 1 }}>
                                            <TextField
                                                fullWidth
                                                multiline
                                                minRows={2}
                                                maxRows={4}
                                                size="small"
                                                disabled
                                                placeholder={t(
                                                    "puzzle.playCross.assistant-placeholder",
                                                    "Ask for a hint about this puzzle"
                                                )}
                                                sx={{
                                                    flex: 1,
                                                    minWidth: 0,
                                                    "& .MuiOutlinedInput-root": { borderRadius: 2 },
                                                }}
                                                inputProps={{
                                                    "aria-label": t("puzzle.playCross.assistant-placeholder", "Message"),
                                                }}
                                            />
                                            <Button
                                                variant="contained"
                                                color="secondary"
                                                disabled
                                                sx={{
                                                    flexShrink: 0,
                                                    alignSelf: "flex-end",
                                                    mb: "2px",
                                                    textTransform: "none",
                                                }}
                                            >
                                                {t("puzzle.playCross.assistant-send", "Send")}
                                            </Button>
                                        </Box>
                                    </Box>
                                </Box>
                            </Box>
                        </Box>

                        <Divider sx={{ my: 4 }} />

                        <Box sx={{ mb: 5 }}>
                            <Typography variant="h5" component="h2" gutterBottom sx={{ mb: 2, fontWeight: 600 }}>
                                {t("howto.assistant.section-ideas")}
                            </Typography>
                            <IdeaBlock title={t("howto.assistant.idea-meanings-title")} body={t("howto.assistant.idea-meanings-body")} />
                            <IdeaBlock title={t("howto.assistant.idea-sentence-title")} body={t("howto.assistant.idea-sentence-body")} />
                            <IdeaBlock title={t("howto.assistant.idea-hint-title")} body={t("howto.assistant.idea-hint-body")} />
                            <IdeaBlock title={t("howto.assistant.idea-roots-title")} body={t("howto.assistant.idea-roots-body")} />
                        </Box>

                        <Divider sx={{ my: 4 }} />

                        <Box sx={{ mb: 2 }}>
                            <Typography variant="h5" component="h2" gutterBottom sx={{ mb: 2, fontWeight: 600 }}>
                                {t("howto.assistant.section-notes")}
                            </Typography>
                            <NoteRow title={t("howto.assistant.note-signin-title")} body={t("howto.assistant.note-signin-body")} />
                            <NoteRow title={t("howto.assistant.note-credits-title")} body={t("howto.assistant.note-credits-body")} />
                            <NoteRow title={t("howto.assistant.note-context-title")} body={t("howto.assistant.note-context-body")} />
                            <Typography variant="body1" sx={{ mt: 2, fontStyle: "italic", fontSize: "1.1rem", lineHeight: 1.8, color: "text.secondary" }}>
                                {t("howto.assistant.closing")}
                            </Typography>
                        </Box>
                    </Item>
                </Grid>
            </Grid>
        </Box>
    );
}
